/**
 * web-studio-sites D7 — a site drawing's action arguments, decoded and checked
 * by their published kind before the piece's action runs:
 * - an item must be an entry of the named list in the piece's current state
 *   (an unknown or stale id does nothing);
 * - a value must have its type and stay within its allowed values, range,
 *   length and format;
 * - anything refused logs one console error and the action does not run.
 */
import type { ActionArgSpec, ActionSpec, PieceContract } from '../contract/define';

type Checked = { readonly ok: true; readonly value: unknown } | { readonly ok: false; readonly why: string };

const ok = (value: unknown): Checked => ({ ok: true, value });
const refused = (why: string): Checked => ({ ok: false, why });

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

/** The entries of a list in the state, by its published path: `days[].times` steps into each day's times. */
export function listAt(state: unknown, path: string): unknown[] {
  let at: unknown[] = [state];
  for (const step of path.split('.')) {
    const each = step.endsWith('[]');
    const key = each ? step.slice(0, -2) : step;
    at = at.map((v) => (v && typeof v === 'object' ? (v as Record<string, unknown>)[key] : undefined)).filter((v) => v !== undefined);
    if (each) at = at.flatMap((v) => (Array.isArray(v) ? v : []));
  }
  return at.flatMap((v) => (Array.isArray(v) ? v : []));
}

function checkValue(spec: Extract<ActionArgSpec, { kind: 'value' }>, raw: unknown): Checked {
  if (raw === undefined || raw === null) return spec.optional ? ok(undefined) : refused('is missing');
  if (spec.type === 'number') {
    const n = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : NaN;
    if (!Number.isFinite(n)) return refused(`${JSON.stringify(raw)} is not a number`);
    if ((spec.min !== undefined && n < spec.min) || (spec.max !== undefined && n > spec.max)) {
      return refused(`${n} is outside ${spec.min ?? '-∞'} to ${spec.max ?? '∞'}`);
    }
    return ok(n);
  }
  if (spec.type === 'boolean') {
    if (raw === true || raw === 'true') return ok(true);
    if (raw === false || raw === 'false') return ok(false);
    return refused(`${JSON.stringify(raw)} is not true or false`);
  }
  if (typeof raw !== 'string') return refused(`${JSON.stringify(raw)} is not text`);
  if (spec.values && !spec.values.includes(raw)) return refused(`"${raw}" is not one of ${spec.values.join(', ')}`);
  if (spec.maxLength !== undefined && raw.length > spec.maxLength) return refused(`is longer than ${spec.maxLength} characters`);
  if (spec.format === 'month' && !MONTH.test(raw)) return refused(`"${raw}" is not a month (YYYY-MM)`);
  if (spec.format === 'path' && (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\'))) {
    return refused(`"${raw}" is not an address on this site`);
  }
  return ok(raw);
}

/** One argument, checked against its kind and the piece's current state. */
export function checkArg(spec: ActionArgSpec, raw: unknown, state: unknown): Checked {
  switch (spec.kind) {
    case 'item': {
      if (raw === undefined || raw === null || raw === '') return refused('names no item');
      const entry = listAt(state, spec.list).find((e) => {
        const id = spec.key ? (e as Record<string, unknown> | null)?.[spec.key] : e;
        return id !== undefined && String(id) === String(raw);
      });
      if (entry === undefined) return refused(`"${String(raw)}" is not in ${spec.list}`);
      return ok(spec.key ? (entry as Record<string, unknown>)[spec.key] : entry);
    }
    case 'value':
      return checkValue(spec, raw);
    case 'answers':
      return raw && typeof raw === 'object' ? ok(raw) : refused('are not answers');
  }
}

type AnyAction = (...args: unknown[]) => unknown;

/**
 * The piece's actions as a drawing may call them: each argument checked first.
 * `state` reads the piece's current state at the moment of the call, so an id
 * from a list that has since changed is refused, never sent on.
 */
export function checkedActions(
  contract: PieceContract,
  actions: Readonly<Record<string, unknown>>,
  state: () => unknown,
): Readonly<Record<string, AnyAction>> {
  const out: Record<string, AnyAction> = {};
  for (const [name, spec] of Object.entries(contract.actions)) {
    const run = actions[name];
    if (typeof run !== 'function') continue;
    out[name] = (...raw) => {
      const args: unknown[] = [];
      for (const [i, arg] of spec.args.entries()) {
        const checked = checkArg(arg, raw[i], state());
        if (!checked.ok) {
          console.error(`[telify] ${contract.name}.${name}: ${arg.name} ${checked.why}; nothing done.`);
          return undefined;
        }
        args.push(checked.value);
      }
      return (run as AnyAction)(...args);
    };
  }
  return Object.freeze(out);
}

/** A form control's own value, for a value argument a control carries. */
function controlValue(el: HTMLElement): unknown {
  if (el instanceof HTMLInputElement) return el.type === 'checkbox' ? el.checked : el.value;
  if (el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) return el.value;
  return undefined;
}

/**
 * The arguments an element with `data-action` sends: each from its own
 * attribute (`data-service-id` for `serviceId`), else the first item from
 * `data-item` and the first value from `data-value` or the control's value.
 */
export function argsFrom(el: HTMLElement, spec: ActionSpec): unknown[] {
  let items = 0;
  let values = 0;
  return spec.args.map((arg) => {
    const own = el.dataset[arg.name];
    if (own !== undefined) return own;
    if (arg.kind === 'item' && items++ === 0) return el.dataset.item;
    if (arg.kind === 'value' && values++ === 0) return el.dataset.value ?? controlValue(el);
    return undefined;
  });
}
